import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { useMeta } from "../hooks/useMeta";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { ShareButtons } from "../components/ShareButtons";

const BAB = [
  { nomor: "01", judul: "Ringkasan Kasus", field: "ringkasan_kasus" },
  { nomor: "02", judul: "Kronologi Singkat", field: "kronologi" },
  { nomor: "03", judul: "Langkah Hukum & Strategi", field: "strategi" },
  { nomor: "04", judul: "Hasil Akhir & Dampak", field: "hasil" },
];

const Paragraf = ({ teks }) => (
  <>
    {teks.split(/\n\s*\n/).filter(Boolean).map((p, i) => (
      <p key={i} className="mb-5 text-base font-light leading-relaxed text-[#C9C9C9] last:mb-0">
        {p}
      </p>
    ))}
  </>
);

export default function KasusDetail() {
  const { slug } = useParams();
  const [kasus, setKasus] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useMeta({
    title: kasus?.judul,
    description: kasus?.ringkasan_kasus?.slice(0, 200),
    image: kasus?.gambar ? imgSrc(kasus.gambar) : undefined,
    type: "article",
  });

  useEffect(() => {
    setKasus(null);
    setNotFound(false);
    api
      .get(`/kasus/${slug}`)
      .then((r) => setKasus(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h1 data-testid="kasus-not-found" className="font-serif text-5xl italic text-[#F5F5F5]">
          Kasus tidak ditemukan.
        </h1>
        <Link
          to="/kasus"
          data-testid="kasus-back-button"
          className="mt-8 inline-flex items-center gap-3 border border-gold/50 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-ink"
        >
          <ArrowLeft size={15} /> Kembali ke Direktori
        </Link>
      </section>
    );
  }

  if (!kasus) {
    return (
      <section className="flex min-h-screen items-center justify-center">
        <p className="font-serif text-2xl italic text-[#737373]">Memuat…</p>
      </section>
    );
  }

  return (
    <>
      <article data-testid="kasus-detail" className="pt-36">
        <div className="mx-auto w-full max-w-4xl px-6">
          <FadeUp>
            <Link
              to="/kasus"
              data-testid="kasus-back-link"
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#A3A3A3] transition-colors hover:text-gold"
            >
              <ArrowLeft size={14} /> Direktori Kasus
            </Link>
          </FadeUp>

          <div className="mt-10">
            <Eyebrow>{kasus.kategori} — {kasus.tanggal}</Eyebrow>
          </div>

          <h1 className="mt-8 font-serif text-4xl leading-[1.1] text-[#F5F5F5] sm:text-5xl">
            <LineReveal lines={[kasus.judul]} delay={0.15} />
          </h1>
        </div>

        {kasus.gambar && (
          <FadeUp delay={0.3} className="mx-auto mt-12 w-full max-w-6xl px-6">
            <div className="overflow-hidden border border-white/10">
              <img src={imgSrc(kasus.gambar)} alt={kasus.judul} className="aspect-[21/9] w-full object-cover" />
            </div>
          </FadeUp>
        )}

        <div className="mx-auto w-full max-w-4xl px-6 py-16">
          {BAB.map((bab, i) => (
            <FadeUp key={bab.nomor} delay={0.05 * i}>
              <section data-testid={`kasus-bab-${bab.nomor}`} className="border-t border-white/15 py-12 first:border-t-0 first:pt-0">
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-4xl italic text-gold/50">{bab.nomor}</span>
                  <h2 className="font-serif text-3xl text-[#F5F5F5]">{bab.judul}</h2>
                </div>
                <div className="mt-6 lg:pl-[4.25rem]">
                  <Paragraf teks={kasus[bab.field]} />
                </div>
              </section>
            </FadeUp>
          ))}

          <div className="mt-6 border-t border-white/10 pt-10">
            <ShareButtons
              url={window.location.href}
              title={kasus.judul}
              imageUrl={kasus.gambar ? imgSrc(kasus.gambar) : undefined}
              testidPrefix="share-kasus"
            />
          </div>

          <p className="mt-10 text-xs font-light italic text-[#737373]">
            Catatan: identitas klien dan detail tertentu disamarkan untuk menjaga kerahasiaan.
          </p>
        </div>
      </article>

      <CtaSection
        testid="kasus-detail-cta"
        lines={[
          "Menghadapi situasi serupa?",
          <span key="t" className="italic text-gold">
            Kami siap mendampingi.
          </span>,
        ]}
      />
    </>
  );
}
